import { useState } from 'react';
import { Package } from 'lucide-react';
import { BRAND_ASSETS } from '../config/brandAssets';

/**
 * Universal product image renderer with a 3-tier fallback chain.
 *
 * Priority (resolved dynamically at render time — the product document
 * is never mutated to embed the brand logo, so the fallback stays live
 * when the brand asset is rotated):
 *
 *     1. product.image_url       (the actual product photo)
 *     2. product.brand_logo_url  (the assigned brand's logo)
 *     3. BRAND_ASSETS.LOGO_PRIMARY  (company logo, R2-hosted)
 *
 * If an image at tier N returns 404 / network-fails, `onError` bumps
 * the tier by one and re-renders — the browser natively retries with
 * the next URL. When ALL three fail we render a neutral `<Package>`
 * icon (rare — only if the R2 bucket is misconfigured).
 *
 * Accepts standard <img> props (`className`, `alt`, `loading`, `sizes`,
 * `style`) so callers can theme it without wrapping. `loading="lazy"`
 * by default preserves the existing catalog perf profile.
 */
export const ProductImage = ({
    product = {},
    brand,                 // optional brand doc if the caller has it (admin lists)
    alt,
    className = '',
    imgClassName = '',
    loading = 'lazy',
    testid,
    onErrorFallbackIcon = true,
}) => {
    const chain = [
        product.image_url,
        product.brand_logo_url || brand?.logo_url,
        BRAND_ASSETS.LOGO_PRIMARY,
    ].filter(Boolean);

    const [tier, setTier] = useState(0);
    const url = chain[tier];

    // No image at all — surface the graceful icon fallback.
    if (!url) {
        return onErrorFallbackIcon ? (
            <div
                className={`flex items-center justify-center bg-gradient-to-br from-[#FBF8F6] to-[#F5EEEA] ${className}`}
                data-testid={testid || 'product-image-fallback'}
            >
                <Package size={40} className="text-gray-300" />
            </div>
        ) : null;
    }

    // For the top-tier image we use `object-cover` (fills the card).
    // Once we fall back to a logo, `object-contain` prevents brand
    // wordmarks from being awkwardly cropped.
    const objectFit = tier === 0 ? 'object-cover' : 'object-contain p-4';

    return (
        <img
            src={url}
            alt={alt || product.name || ''}
            loading={loading}
            className={`w-full h-full ${objectFit} ${imgClassName} ${className}`}
            data-testid={testid ? `${testid}-tier-${tier}` : `product-image-tier-${tier}`}
            onError={() => {
                if (tier + 1 < chain.length) setTier(tier + 1);
            }}
        />
    );
};

export default ProductImage;
